import { useCallback, useEffect, useRef, useState } from 'react';

import { usePendingPosts } from '@/src/features/offline/use-outbox';
import { loadCachedPosts, saveCachedPosts } from '@/src/features/posts/feed-cache';
import {
  PUBLIC_POST_LIMIT,
  subscribeToUserPosts,
  type PublicPost,
} from '@/src/features/posts/post-service';

type UserPostsState = {
  error: Error | null;
  loading: boolean;
  posts: PublicPost[];
};

export function useUserPosts(
  uid: string | undefined,
  currentUid?: string | undefined,
  limitCount = PUBLIC_POST_LIMIT,
  enabled = true,
) {
  const [effectiveLimit, setEffectiveLimit] = useState(limitCount);
  const [loadingMore, setLoadingMore] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshNonce, setRefreshNonce] = useState(0);
  const postsRef = useRef<PublicPost[]>([]);
  const uidRef = useRef<string | undefined>(undefined);
  const pendingPosts = usePendingPosts(uid);
  const [state, setState] = useState<UserPostsState>({
    error: null,
    loading: Boolean(uid),
    posts: [],
  });

  useEffect(() => {
    setEffectiveLimit(limitCount);
  }, [limitCount]);

  useEffect(() => {
    if (uidRef.current !== uid) {
      uidRef.current = uid;
      postsRef.current = [];
    }

    if (!uid || !enabled) {
      postsRef.current = [];
      setLoadingMore(false);
      setRefreshing(false);
      setState({ error: null, loading: false, posts: [] });
      return undefined;
    }

    setState((currentState) => ({
      ...currentState,
      error: null,
      loading: postsRef.current.length === 0,
      posts: postsRef.current.length === 0 ? [] : currentState.posts,
    }));

    if (postsRef.current.length === 0) {
      void loadCachedPosts(`user:${uid}`).then((cachedPosts) => {
        if (cachedPosts.length > 0) {
          postsRef.current = cachedPosts;
          setState((currentState) => ({
            ...currentState,
            loading: false,
            posts: cachedPosts,
          }));
        }
      });
    }

    return subscribeToUserPosts({
      currentUid,
      limitCount: effectiveLimit,
      uid,
      onError: (error) => {
        setState((currentState) => ({
          ...currentState,
          error,
          loading: false,
        }));
        setLoadingMore(false);
        setRefreshing(false);
      },
      onNext: (posts) => {
        postsRef.current = posts;
        setState({ error: null, loading: false, posts });
        setLoadingMore(false);
        setRefreshing(false);
        void saveCachedPosts(`user:${uid}`, posts);
      },
    });
  }, [currentUid, effectiveLimit, enabled, refreshNonce, uid]);

  const hasMore = state.posts.length >= effectiveLimit;

  const loadMore = useCallback(() => {
    if (state.loading || loadingMore || refreshing || !hasMore) {
      return;
    }

    setLoadingMore(true);
    setEffectiveLimit((currentLimit) => currentLimit + PUBLIC_POST_LIMIT);
  }, [hasMore, loadingMore, refreshing, state.loading]);

  const refresh = useCallback(() => {
    if (!uid) {
      return;
    }

    setRefreshing(true);
    setRefreshNonce((currentNonce) => currentNonce + 1);
  }, [uid]);

  return {
    ...state,
    hasMore,
    loadMore,
    loadingMore,
    posts: [...pendingPosts, ...state.posts],
    refresh,
    refreshing,
  };
}
